/* ──────────────────────────────────────────────────────────────────────
   TwinMsCasePage — cinematic "dossier" detail page for a single use case.
   URL: /twinms/use-cases/<slug>/
   Reads the slug from window.location.pathname and renders the matching
   entry in window.DZ_TWINMS.cases.

   Page layout, in order:
     · Hero with marquee video, dossier topline, meta strip
     · Specifications spread (typeset spec sheet)
     · Chapter 01 — Inside the scenario  (numbered "scene notes")
     · Chapter 02 — The mechanics        (rows with monochrome figures)
     · Chapter 03 — The outcomes         (oversized numerals + lines)
     · Continue the dossier              (cross-links + editorial CTA)
   ────────────────────────────────────────────────────────────────────── */

const { useState: useTmcState, useEffect: useTmcEffect, useRef: useTmcRef } = React;

// Path-based AR localization for hardcoded UI labels (content from content/twinms_ar.js).
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }

function useTmcReveal(threshold) {
  const ref = useTmcRef(null);
  useTmcEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          el.classList.add('is-in-view');
          io.disconnect();
        }
      });
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return ref;
}

/* ── Reading-progress bar (RAF + scroll) ──────────────────────────────── */
function TmcReadingProgress() {
  const barRef = useTmcRef(null);
  useTmcEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const bar = barRef.current;
        if (!bar) { raf = 0; return; }
        const h    = document.documentElement;
        const top  = h.scrollTop || document.body.scrollTop;
        const max  = (h.scrollHeight - h.clientHeight) || 1;
        bar.style.transform = `scaleX(${Math.min(1, top / max)})`;
        raf = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="dz-tmsc-progress"><div className="dz-tmsc-progress__bar" ref={barRef} /></div>;
}

/* ── Sticky chapter rail (frosted-glass capsule) ──────────────────────── */
function TmcChapterRail({ chapters, scrollToId }) {
  const [active, setActive] = useTmcState(0);

  useTmcEffect(() => {
    const targets = chapters.map((c) => document.getElementById(c.id)).filter(Boolean);
    if (!targets.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const idx = targets.findIndex((t) => t === e.target);
          if (idx >= 0) setActive(idx);
        }
      });
    }, { rootMargin: '-40% 0px -50% 0px', threshold: 0 });
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [chapters]);

  return (
    <aside className="dz-tmsc-rail" aria-hidden="true">
      {chapters.map((c, i) => (
        <button
          key={c.id}
          className={`dz-tmsc-rail__dot ${i === active ? 'is-active' : ''}`}
          onClick={() => scrollToId(c.id)}
          tabIndex={-1}
        >
          <span className="dz-tmsc-rail__num">{c.num}</span>
          <span className="dz-tmsc-rail__lbl">{c.short}</span>
        </button>
      ))}
    </aside>
  );
}

/* ── CUSTOM VIDEO PLAYER ──────────────────────────────────────────────────
   Always-visible control bar pinned to the bottom of the media card:
   play/pause, scrubber + time, mute toggle. Replaces the auto-hiding
   native HTML5 controls so visitors always see how to start the video.

   Renders the <video> + the controls bar; in TmcHero the video element
   is placed inside .dz-tmsc-hero__media-shift (so it parallaxes) and
   the controls bar is placed as a sibling of the shift, inside the
   .dz-tmsc-hero__media card (so it stays anchored to the bottom of the
   card regardless of the parallax translate).
   ────────────────────────────────────────────────────────────────────── */
function TmcVideoControls({ videoRef }) {
  const scrubRef = useTmcRef(null);
  const [playing,     setPlaying]     = useTmcState(false);
  const [muted,       setMuted]       = useTmcState(false);
  const [currentTime, setCurrentTime] = useTmcState(0);
  const [duration,    setDuration]    = useTmcState(0);
  const [scrubbing,   setScrubbing]   = useTmcState(false);
  const [shared,      setShared]      = useTmcState(false);
  const [isFs,        setIsFs]        = useTmcState(false);

  /* Sync fullscreen-button icon with the actual fullscreen state.
     Listen on document for both standard + webkit-prefixed events. */
  useTmcEffect(() => {
    const sync = () => setIsFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange',       sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange',       sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  function toggleFullscreen() {
    const v = videoRef.current;
    if (!v) return;
    /* Already fullscreen → exit */
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen)       { document.exitFullscreen(); }
      else if (document.webkitExitFullscreen) { document.webkitExitFullscreen(); }
      return;
    }
    /* iOS Safari mobile — Fullscreen API isn't available on the document/element
       level; only <video>.webkitEnterFullscreen works. This keeps native iOS
       fullscreen working at the cost of losing our custom controls in that case. */
    const card = v.closest('.dz-tmsc-hero__media');
    if (card && card.requestFullscreen) {
      const p = card.requestFullscreen();
      if (p && p.catch) p.catch(() => {});
    } else if (card && card.webkitRequestFullscreen) {
      card.webkitRequestFullscreen();
    } else if (v.webkitEnterFullscreen) {
      v.webkitEnterFullscreen();
    }
  }

  function shareVideo() {
    const url   = window.location.href;
    const title = document.title;
    const text  = (document.querySelector('meta[name="description"]') || {}).content || '';
    /* Web Share API on mobile + Safari + Chrome >= 89 desktop on https. */
    if (navigator.share) {
      navigator.share({ url, title, text }).catch(() => {});
      return;
    }
    /* Fallback: copy URL to clipboard + show 2s "Copied" confirmation */
    const onSuccess = () => { setShared(true); setTimeout(() => setShared(false), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(onSuccess).catch(() => {});
    } else {
      /* execCommand legacy fallback for older browsers */
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); onSuccess(); } catch (e) {}
      document.body.removeChild(ta);
    }
  }

  /* Wire up listeners on the externally-owned video element */
  useTmcEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay   = ()  => setPlaying(true);
    const onPause  = ()  => setPlaying(false);
    const onTime   = ()  => setCurrentTime(v.currentTime);
    const onMeta   = ()  => { setDuration(v.duration); setMuted(v.muted); };
    const onVolume = ()  => setMuted(v.muted);
    v.addEventListener('play',           onPlay);
    v.addEventListener('pause',          onPause);
    v.addEventListener('timeupdate',     onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('volumechange',   onVolume);
    /* If metadata is already loaded (cached), sync now */
    if (v.readyState >= 1) onMeta();
    return () => {
      v.removeEventListener('play',           onPlay);
      v.removeEventListener('pause',          onPause);
      v.removeEventListener('timeupdate',     onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('volumechange',   onVolume);
    };
  }, []);

  function fmt(t) {
    if (!isFinite(t) || t < 0) t = 0;
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
    else          { v.pause(); }
  }

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }

  function pctFromEvent(e) {
    const bar = scrubRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  }

  function seekToEvent(e) {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = pctFromEvent(e) * v.duration;
  }

  function onScrubStart(e) {
    setScrubbing(true);
    seekToEvent(e);
    e.preventDefault();
  }

  /* Window-level pointermove / pointerup so dragging continues outside the bar */
  useTmcEffect(() => {
    if (!scrubbing) return;
    const onMove = (e) => seekToEvent(e);
    const onUp   = ()  => setScrubbing(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup',   onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup',   onUp);
    };
  });

  const pct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="dz-tmsc-hero__player" role="group" aria-label="Video controls">
      <button type="button"
              className="dz-tmsc-hero__player-btn"
              onClick={togglePlay}
              aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7 5v14l12-7L7 5z" />
          </svg>
        )}
      </button>

      <div className="dz-tmsc-hero__player-scrub-wrap">
        <div className="dz-tmsc-hero__player-scrub"
             ref={scrubRef}
             role="slider"
             aria-label="Seek"
             aria-valuemin="0"
             aria-valuemax={Math.round(duration) || 0}
             aria-valuenow={Math.round(currentTime)}
             onPointerDown={onScrubStart}>
          <div className="dz-tmsc-hero__player-scrub-fill" style={{ width: pct + '%' }} />
          <div className="dz-tmsc-hero__player-scrub-thumb" style={{ left: pct + '%' }} />
        </div>
      </div>

      <div className="dz-tmsc-hero__player-time" aria-hidden="true">
        <span>{fmt(currentTime)}</span>
        <span className="dz-tmsc-hero__player-time-sep"> / </span>
        <span className="dz-tmsc-hero__player-time-total">{fmt(duration)}</span>
      </div>

      <button type="button"
              className="dz-tmsc-hero__player-btn dz-tmsc-hero__player-btn--mute"
              onClick={toggleMute}
              aria-label={muted ? 'Unmute' : 'Mute'}>
        {muted ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="22" y1="9"  x2="16" y2="15" strokeLinecap="round" />
            <line x1="16" y1="9"  x2="22" y2="15" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      <button type="button"
              className={'dz-tmsc-hero__player-btn dz-tmsc-hero__player-btn--share' + (shared ? ' is-shared' : '')}
              onClick={shareVideo}
              aria-label="Share this video">
        {shared ? (
          /* checkmark = link copied */
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <polyline points="5 12 10 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          /* universal share-arrow glyph */
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="16 6 12 2 8 6" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="12" y1="2" x2="12" y2="15" strokeLinecap="round" />
          </svg>
        )}
      </button>
      {shared && <span className="dz-tmsc-hero__player-toast" role="status">{dzT('Link copied', 'تم نسخ الرابط')}</span>}

      <button type="button"
              className="dz-tmsc-hero__player-btn dz-tmsc-hero__player-btn--fs"
              onClick={toggleFullscreen}
              aria-label={isFs ? 'Exit fullscreen' : 'Enter fullscreen'}>
        {isFs ? (
          /* contract icon = exit fullscreen */
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="4 10 10 10 10 4"   strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="20 10 14 10 14 4"  strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="14 20 14 14 20 14" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="10 20 10 14 4 14"  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          /* expand icon = enter fullscreen */
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="9 3 3 3 3 9"     strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="15 3 21 3 21 9"  strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="21 15 21 21 15 21" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="3 15 3 21 9 21"  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );
}

/* ── HERO ─────────────────────────────────────────────────────────────── */
function TmcHero({ data }) {
  const ref       = useTmcReveal(0.05);
  const shiftRef  = useTmcRef(null);
  const videoRef  = useTmcRef(null);

  /* Subtle parallax on the video frame — translates downward as the user
     scrolls past the hero. RAF-throttled, capped at 60px, disabled if the
     user prefers reduced motion. */
  useTmcEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const el = shiftRef.current;
        if (!el) { raf = 0; return; }
        const y = window.scrollY || 0;
        const offset = Math.min(60, Math.max(0, y * 0.18));
        el.style.transform = `translate3d(0, ${offset}px, 0)`;
        raf = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="dz-tmsc-hero" ref={ref}>
      <div className="dz-tmsc-hero__inner">
        <div className="dz-tmsc-hero__crumb">
          <a href={dzT('/', '/ar/')}>{dzT('Home', 'الرئيسية')}</a>
          <span>/</span>
          <a href={dzT('/twinms/', '/ar/twinms/')}>TwinMs</a>
          <span>/</span>
          <a href={dzT('/twinms/#use-cases', '/ar/twinms/#use-cases')}>{dzT('Use cases', 'حالات الاستخدام')}</a>
          <span>/</span>
          <span className="is-current">{data.breadcrumb}</span>
        </div>

        <h1 className="dz-tmsc-hero__title">{data.title}</h1>
        <p className="dz-tmsc-hero__lead">{data.lead}</p>

        <div className="dz-tmsc-hero__media">
          <div className="dz-tmsc-hero__media-shift" ref={shiftRef}>
            <video
              ref={videoRef}
              className="dz-tmsc-hero__video"
              playsInline
              preload="metadata"
              poster={data.poster || undefined}
              src={data.video}
              onClick={() => {
                const v = videoRef.current;
                if (!v) return;
                if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
                else          { v.pause(); }
              }}
            />
            <div className="dz-tmsc-hero__media-edge" aria-hidden="true" />
          </div>
          <TmcVideoControls videoRef={videoRef} />
          <div className="dz-tmsc-hero__hint">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Press play — voiceover walkthrough
          </div>
        </div>

        {data.meta && (
          <div className="dz-tmsc-hero__meta">
            {data.meta.map((m, i) => (
              <div className="dz-tmsc-hero__meta-cell" key={i}>
                <div className="dz-tmsc-hero__meta-lbl">{m.label}</div>
                <div className="dz-tmsc-hero__meta-val">{m.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Specifications spread — typeset spec sheet ───────────────────────── */
function TmcSpecs({ specs, dossier }) {
  const ref = useTmcReveal(0.12);
  if (!specs) return null;
  return (
    <section className="dz-tmsc-specs" ref={ref}>
      <div className="dz-tmsc-specs__inner">
        <div className="dz-tmsc-specs__head">
          <span className="dz-tmsc-specs__rule" aria-hidden="true" />
          <span className="dz-tmsc-specs__kicker">Dossier {dossier} — Specifications</span>
        </div>
        <h2 className="dz-tmsc-specs__title">{specs.title}.</h2>
        <dl className="dz-tmsc-specs__list">
          {specs.rows.map((r, i) => (
            <div className="dz-tmsc-specs__row" key={i} style={{ '--tmc-i': i }}>
              <dt className="dz-tmsc-specs__lbl">{r.label}</dt>
              <dd className="dz-tmsc-specs__val">{r.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ── Generic Chapter — heavy editorial header used by every chapter ──── */
function TmcChapter({ id, num, kicker, title, theme, children }) {
  const ref = useTmcReveal(0.12);
  return (
    <section
      id={id}
      ref={ref}
      className={`dz-tmsc-ch dz-tmsc-ch--${theme}`}
      data-chapter={num}
    >
      <div className="dz-tmsc-ch__watermark" aria-hidden="true">{num}</div>
      <div className="dz-tmsc-ch__inner">
        <div className="dz-tmsc-ch__header">
          <span className="dz-tmsc-ch__rule" aria-hidden="true" />
          <span className="dz-tmsc-ch__kicker">{num} — {kicker}</span>
        </div>
        <h2 className="dz-tmsc-ch__title">{title}</h2>
        <div className="dz-tmsc-ch__body">{children}</div>
      </div>
    </section>
  );
}

/* ── Chapter 01 body — scene notes ────────────────────────────────────── */
function TmcShowsBody({ items, dossier }) {
  return (
    <ol className="dz-tmsc-shows">
      {items.map((s, i) => (
        <li key={i} style={{ '--tmc-i': i }}>
          <div className="dz-tmsc-shows__idx">
            <span className="dz-tmsc-shows__idx-num">Scene {dossier}.{i + 1}</span>
            <span className="dz-tmsc-shows__idx-scene">{s.scene}</span>
          </div>
          <span className="dz-tmsc-shows__txt">{s.text}</span>
        </li>
      ))}
    </ol>
  );
}

/* ── Monochrome figure glyphs for chapter 02 ──────────────────────────── */
function FigureGlyph({ name }) {
  // 64×64 canvas, 1.75 stroke, currentColor — feels like a journal figure.
  const common = {
    width:  '64',
    height: '64',
    viewBox:'0 0 64 64',
    fill:   'none',
    stroke: 'currentColor',
    strokeWidth: '1.75',
    strokeLinecap:'round',
    strokeLinejoin:'round'
  };

  switch (name) {
    case 'broadcast': return (
      <svg {...common}>
        <circle cx="32" cy="40" r="2.5" fill="currentColor" stroke="none" />
        <path d="M22 40a10 10 0 0 1 20 0">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="2.4s" repeatCount="indefinite" />
        </path>
        <path d="M16 40a16 16 0 0 1 32 0">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="2.4s" begin="0.3s" repeatCount="indefinite" />
        </path>
        <path d="M10 40a22 22 0 0 1 44 0">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="2.4s" begin="0.6s" repeatCount="indefinite" />
        </path>
      </svg>
    );
    case 'curve': return (
      <svg {...common}>
        <path id="dz-curve-path" d="M6 40 Q 16 22, 26 32 T 46 36 T 58 24" />
        <line x1="6" y1="48" x2="58" y2="48" strokeDasharray="2 3" opacity="0.5" />
        <circle r="2.5" fill="currentColor" stroke="none">
          <animateMotion dur="3.2s" repeatCount="indefinite" rotate="auto">
            <mpath href="#dz-curve-path" />
          </animateMotion>
        </circle>
      </svg>
    );
    case 'alert': return (
      <svg {...common}>
        <path d="M32 14 L52 46 H12 Z" />
        <line x1="32" y1="26" x2="32" y2="36" />
        <circle cx="32" cy="41" r="1.5" fill="currentColor" stroke="none" />
        <circle cx="48" cy="18" r="3" opacity="0.5">
          <animate attributeName="r" values="3;6;3" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="48" cy="18" r="6" opacity="0.25">
          <animate attributeName="r" values="6;9;6" dur="2s" begin="0.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" begin="0.4s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'phone': return (
      <svg {...common}>
        <rect x="20" y="8" width="24" height="48" rx="4" />
        <line x1="28" y1="14" x2="36" y2="14" opacity="0.6" />
        <circle cx="32" cy="50" r="1.5" fill="currentColor" stroke="none" />
        <circle cx="40" cy="14" r="2.5" fill="currentColor" stroke="none">
          <animate attributeName="r" values="2.5;3.6;2.5" dur="1.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.5;1" dur="1.6s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'network': return (
      <svg {...common}>
        <line x1="32" y1="18" x2="14" y2="42" strokeDasharray="40" strokeDashoffset="40">
          <animate attributeName="stroke-dashoffset" values="40;0;0;40" keyTimes="0;0.4;0.7;1" dur="3.6s" repeatCount="indefinite" />
        </line>
        <line x1="32" y1="18" x2="50" y2="42" strokeDasharray="40" strokeDashoffset="40">
          <animate attributeName="stroke-dashoffset" values="40;0;0;40" keyTimes="0;0.4;0.7;1" dur="3.6s" begin="0.3s" repeatCount="indefinite" />
        </line>
        <line x1="18" y1="46" x2="46" y2="46" strokeDasharray="32" strokeDashoffset="32">
          <animate attributeName="stroke-dashoffset" values="32;0;0;32" keyTimes="0;0.4;0.7;1" dur="3.6s" begin="0.6s" repeatCount="indefinite" />
        </line>
        <circle cx="32" cy="14" r="3.5" fill="currentColor" stroke="currentColor" />
        <circle cx="14" cy="46" r="3.5" fill="currentColor" stroke="currentColor" />
        <circle cx="50" cy="46" r="3.5" fill="currentColor" stroke="currentColor" />
      </svg>
    );
    case 'tag': return (
      <svg {...common}>
        <path d="M12 20 L32 8 L52 20 V44 L32 56 L12 44 Z" />
        <circle cx="32" cy="32" r="3.5" fill="currentColor" stroke="none" />
        <path d="M40 24a10 10 0 0 1 0 16">
          <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
        </path>
        <path d="M44 20a16 16 0 0 1 0 24" opacity="0.6">
          <animate attributeName="opacity" values="0.2;0.7;0.2" dur="2s" begin="0.3s" repeatCount="indefinite" />
        </path>
      </svg>
    );
    case 'receipt': return (
      <svg {...common}>
        <path d="M16 6 V58 L22 54 L28 58 L34 54 L40 58 L46 54 L48 58 V6 Z" />
        <line x1="22" y1="20" x2="42" y2="20" />
        <line x1="22" y1="28" x2="42" y2="28" />
        <line x1="22" y1="36" x2="42" y2="36" opacity="0.6" />
        <line x1="22" y1="44" x2="36" y2="44" opacity="0.6" />
      </svg>
    );
    case 'checklist': return (
      <svg {...common}>
        <rect x="14" y="10" width="36" height="44" rx="2" />
        <polyline points="20,22 24,26 30,18" strokeDasharray="20" strokeDashoffset="20">
          <animate attributeName="stroke-dashoffset" values="20;0;0;20" keyTimes="0;0.35;0.85;1" dur="3.4s" repeatCount="indefinite" />
        </polyline>
        <line x1="34" y1="22" x2="44" y2="22" opacity="0.7" />
        <polyline points="20,36 24,40 30,32" strokeDasharray="20" strokeDashoffset="20">
          <animate attributeName="stroke-dashoffset" values="20;0;0;20" keyTimes="0;0.35;0.85;1" dur="3.4s" begin="1.1s" repeatCount="indefinite" />
        </polyline>
        <line x1="34" y1="36" x2="44" y2="36" opacity="0.7" />
        <line x1="20" y1="48" x2="44" y2="48" opacity="0.4" />
      </svg>
    );
    case 'chart': return (
      <svg {...common}>
        <line x1="10" y1="54" x2="58" y2="54" />
        <rect x="14" y="36" width="8"  height="18">
          <animate attributeName="height" values="0;18;18" keyTimes="0;0.5;1" dur="3s" repeatCount="indefinite" />
          <animate attributeName="y" values="54;36;36" keyTimes="0;0.5;1" dur="3s" repeatCount="indefinite" />
        </rect>
        <rect x="28" y="22" width="8"  height="32">
          <animate attributeName="height" values="0;32;32" keyTimes="0;0.5;1" dur="3s" begin="0.2s" repeatCount="indefinite" />
          <animate attributeName="y" values="54;22;22" keyTimes="0;0.5;1" dur="3s" begin="0.2s" repeatCount="indefinite" />
        </rect>
        <rect x="42" y="44" width="8"  height="10">
          <animate attributeName="height" values="0;10;10" keyTimes="0;0.5;1" dur="3s" begin="0.4s" repeatCount="indefinite" />
          <animate attributeName="y" values="54;44;44" keyTimes="0;0.5;1" dur="3s" begin="0.4s" repeatCount="indefinite" />
        </rect>
        <line x1="10" y1="14" x2="10" y2="54" opacity="0.5" />
      </svg>
    );
    case 'camera': return (
      <svg {...common}>
        <rect x="10" y="20" width="34" height="28" rx="2" />
        <path d="M44 28 L56 22 V46 L44 40 Z" />
        <circle cx="20" cy="34" r="3">
          <animate attributeName="r" values="3;5;3" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="20" cy="34" r="6" opacity="0.4">
          <animate attributeName="r" values="6;9;6" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0;0.5" dur="2.4s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'heatmap': return (
      <svg {...common}>
        {[0,1,2,3].map((r) =>
          [0,1,2,3].map((c) => {
            const ops = [
              [0.15,0.30,0.55,0.85],
              [0.25,0.55,0.85,0.65],
              [0.35,0.65,0.55,0.30],
              [0.20,0.30,0.30,0.15]
            ];
            const base = ops[r][c];
            const peak = Math.min(0.95, base + 0.35);
            const delay = ((r * 4 + c) * 0.12) % 2.4;
            return (
              <rect
                key={`${r}-${c}`}
                x={8 + c*12}
                y={8 + r*12}
                width="11"
                height="11"
                fill="currentColor"
                stroke="none"
                opacity={base}
              >
                <animate
                  attributeName="opacity"
                  values={`${base};${peak};${base}`}
                  dur="3.2s"
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
              </rect>
            );
          })
        )}
      </svg>
    );
    case 'arrows': return (
      <svg {...common}>
        <rect x="8" y="8" width="48" height="48" rx="2" opacity="0.4" />
        <path d="M16 32 H48" />
        <polyline points="42,26 48,32 42,38" />
        <path d="M32 16 V48" opacity="0.55" />
        <polyline points="26,42 32,48 38,42" opacity="0.55" />
      </svg>
    );
    case 'hub': return (
      <svg {...common}>
        <line x1="32" y1="13" x2="32" y2="26" />
        <line x1="51" y1="32" x2="38" y2="32" />
        <line x1="32" y1="51" x2="32" y2="38" />
        <line x1="13" y1="32" x2="26" y2="32" />
        <circle cx="32" cy="32" r="6" />
        <circle cx="32" cy="10" r="3">
          <animate attributeName="r" values="3;4.4;3" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.55;1" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="54" cy="32" r="3">
          <animate attributeName="r" values="3;4.4;3" dur="2.4s" begin="0.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.55;1" dur="2.4s" begin="0.6s" repeatCount="indefinite" />
        </circle>
        <circle cx="32" cy="54" r="3">
          <animate attributeName="r" values="3;4.4;3" dur="2.4s" begin="1.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.55;1" dur="2.4s" begin="1.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="10" cy="32" r="3">
          <animate attributeName="r" values="3;4.4;3" dur="2.4s" begin="1.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.55;1" dur="2.4s" begin="1.8s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    default: return (
      <svg {...common}>
        <circle cx="32" cy="32" r="20" />
      </svg>
    );
  }
}

/* ── Chapter 02 body — figures + kickers + paragraphs ─────────────────── */
function TmcAppliesBody({ items, dossier }) {
  return (
    <div className="dz-tmsc-applies">
      {items.map((a, i) => (
        <article className="dz-tmsc-apply" key={i} style={{ '--tmc-i': i }}>
          <div className="dz-tmsc-apply__col">
            <div className="dz-tmsc-apply__fig">
              <FigureGlyph name={a.figure} />
            </div>
            <div className="dz-tmsc-apply__caption">Fig. {dossier}.{i + 1}</div>
            <div className="dz-tmsc-apply__kicker">{a.kicker}</div>
          </div>
          <p className="dz-tmsc-apply__text">{a.text}</p>
        </article>
      ))}
    </div>
  );
}

/* ── Numeral count-up — for numeric values like "<50ms", "730 d", "216" ─ */
function TmcCountNumeral({ value, className }) {
  const ref = useTmcRef(null);

  // Parse: optional non-digit prefix, digits (with optional decimal), optional non-digit suffix.
  const m = typeof value === 'string'
    ? value.match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/)
    : null;
  const isNumeric = !!m;
  const prefix    = isNumeric ? m[1] : '';
  const target    = isNumeric ? parseFloat(m[2]) : 0;
  const decimals  = isNumeric && m[2].includes('.') ? m[2].split('.')[1].length : 0;
  const suffix    = isNumeric ? m[3] : '';

  const initial = !isNumeric || target === 0
    ? value
    : `${prefix}0${suffix}`;
  const [display, setDisplay] = useTmcState(initial);

  useTmcEffect(() => {
    if (!isNumeric || target === 0) return;
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          io.disconnect();
          const duration = 1400;
          const t0 = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - t0) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const current = target * eased;
            const formatted = decimals > 0
              ? current.toFixed(decimals)
              : String(Math.round(current));
            setDisplay(`${prefix}${formatted}${suffix}`);
            if (t < 1) requestAnimationFrame(tick);
            else setDisplay(`${prefix}${decimals > 0 ? target.toFixed(decimals) : target}${suffix}`);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });

    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return <span ref={ref} className={className || "dz-tmsc-value__num"}>{display}</span>;
}

/* ── Chapter 03 body — editorial bento grid of outcomes ───────────────── */
function TmcValueBody({ items }) {
  return (
    <div className="dz-tmsc-bento">
      {items.map((v, i) => (
        <article
          className={`dz-tmsc-bento__cell ${i === 0 ? 'dz-tmsc-bento__cell--featured' : ''}`}
          key={i}
          style={{ '--tmc-i': i }}
        >
          <div className="dz-tmsc-bento__glyph" aria-hidden="true">
            <FigureGlyph name={v.glyph} />
          </div>
          <span className="dz-tmsc-bento__rule" aria-hidden="true" />
          <p className="dz-tmsc-bento__txt">{v.text}</p>
        </article>
      ))}
    </div>
  );
}

/* ── Continue the dossier — cross-link strip + editorial CTA ──────────── */
function TmcContinue({ currentSlug }) {
  const ref = useTmcReveal(0.15);
  const tiles = (window.DZ_TWINMS && window.DZ_TWINMS.useCases && window.DZ_TWINMS.useCases.tiles) || [];
  const cases = (window.DZ_TWINMS && window.DZ_TWINMS.cases) || {};
  const others = tiles.filter((t) => t.slug !== currentSlug);

  return (
    <section className="dz-tmsc-continue" ref={ref}>
      <div className="dz-tmsc-continue__inner">
        <div className="dz-tmsc-continue__head">
          <span className="dz-tmsc-continue__kicker">— Continue the dossier —</span>
          <h2 className="dz-tmsc-continue__title">See where else TwinMs lives.</h2>
        </div>

        <div className="dz-tmsc-continue__grid">
          {others.map((t, i) => {
            const c = cases[t.slug] || {};
            const pillMod = t.status === 'Live' ? 'live' : 'demo';
            const metaText = t.status === 'Live'
              ? dzT('Live in ', 'مباشر في ') + (t.statusNote || dzT('production', 'الإنتاج'))
              : dzT('Demo scenario', 'عرض توضيحي');
            return (
              <a className="dz-tmsc-continue__card" href={t.href} key={t.slug} style={{ '--tmc-i': i }}>
                <div className="dz-tmsc-continue__card-num">{c.dossier || ''}</div>
                <div className="dz-tmsc-continue__card-body">
                  <p className="dz-tmsc-continue__card-meta">
                    <span className={`dz-tmsc-continue__card-dot dz-tmsc-continue__card-dot--${pillMod}`} aria-hidden="true" />
                    {metaText}
                  </p>
                  <h3 className="dz-tmsc-continue__card-name">{t.label}</h3>
                  <p className="dz-tmsc-continue__card-title">{t.title}</p>
                  <span className="dz-tmsc-continue__card-cta" aria-hidden="true">
                    Open scenario
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
              </a>
            );
          })}
        </div>

        <div className="dz-tmsc-continue__signoff">
          <p className="dz-tmsc-continue__signoff-line">
            <span className="dz-tmsc-continue__signoff-dash" aria-hidden="true">{dzT('— end of dossier —', '— نهاية الملف —')}</span>
          </p>
          <div className="dz-tmsc-continue__actions">
            <a className="dz-tmsc-continue__brief" href="#inquiry">
              {dzT('Request a private briefing', 'اطلب جلسة تعريفية خاصّة')}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a className="dz-tmsc-continue__back" href={dzT('/twinms/', '/ar/twinms/')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {dzT('Back to TwinMs overview', 'العودة إلى النظرة العامة على TwinMs')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Page root ────────────────────────────────────────────────────────── */
function TwinMsCasePage() {
  const m    = window.location.pathname.match(/\/twinms\/use-cases\/([^\/]+)\/?$/);
  const slug = m ? m[1] : null;

  const cases = (window.DZ_TWINMS && window.DZ_TWINMS.cases) || {};
  const data  = slug && cases[slug];

  useTmcEffect(() => {
    if (data && data.title) {
      document.title = data.title.replace(/\.$/, '') + ' — TwinMs · Design Zone';
    }
  }, [data]);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!data) {
    return (
      <main className="dz-tmsc-missing">
        <h1>{dzT('Use case not found.', 'حالة الاستخدام غير موجودة.')}</h1>
        <p>{dzT("We couldn't find that scenario. ", 'تعذّر العثور على هذا السيناريو. ')}<a href={dzT('/twinms/', '/ar/twinms/')}>{dzT('Back to TwinMs', 'العودة إلى TwinMs')}</a>.</p>
      </main>
    );
  }

  const chapters = [
    { id: 'ch-shows',   num: '01', short: dzT('Shows', 'يُظهر') },
    { id: 'ch-applies', num: '02', short: dzT('Applies', 'يُطبَّق') },
    { id: 'ch-value',   num: '03', short: dzT('Value', 'القيمة') }
  ];

  return (
    <main className="dz-tmsc" id="dz-main">
      <TmcReadingProgress />
      <TmcChapterRail chapters={chapters} scrollToId={scrollToId} />

      <TmcHero  data={data} />
      <TmcSpecs specs={data.specs} dossier={data.dossier} />

      <TmcChapter id="ch-shows" num="01" kicker="Inside the scenario"
                  title={data.shows.title} theme="light">
        <TmcShowsBody items={data.shows.items} dossier={data.dossier} />
      </TmcChapter>

      <TmcChapter id="ch-applies" num="02" kicker="The mechanics"
                  title={data.applies.title} theme="dark">
        <TmcAppliesBody items={data.applies.items} dossier={data.dossier} />
      </TmcChapter>

      <TmcChapter id="ch-value" num="03" kicker="The outcomes"
                  title={data.value.title} theme="light">
        <TmcValueBody items={data.value.items} />
      </TmcChapter>

      <TmcContinue currentSlug={slug} />
    </main>
  );
}

window.TwinMsCasePage = TwinMsCasePage;
